import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import GiviLogo from "./GiviLogo";
import { FloatItem, Twinkle } from "./FloatingDecor";

const EASE = [0.16, 1, 0.3, 1];

const HEART_PATH =
  "M12 21c-4.6-3.7-9-7-9-11.2C3 6.6 5.4 4.5 8 4.5c1.6 0 3.1.8 4 2.1.9-1.3 2.4-2.1 4-2.1 2.6 0 5 2.1 5 5.3 0 4.2-4.4 7.5-9 11.2z";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.3, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const HeartScrollCue = () => (
  <motion.a
    href="#services"
    data-testid="scroll-down-heart"
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 2.1, duration: 1, ease: EASE }}
    className="mt-8 md:mt-10 inline-block group z-10"
  >
    <motion.div
      animate={{ y: [0, -9, 0] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      className="flex flex-col items-center gap-3"
    >
      <span className="text-[0.65rem] tracking-[0.45em] uppercase text-[#C57B89] font-medium">
        Scroll down
      </span>
      <div className="relative w-16 h-16 flex items-center justify-center">
        <div
          className="absolute inset-0 rounded-full bg-[#D88C9A]/30 blur-xl scale-125"
          aria-hidden="true"
        />
        <motion.svg
          viewBox="0 0 24 24"
          className="absolute inset-0 w-full h-full"
          fill="none"
          stroke="#E0A3AC"
          strokeWidth="1"
          animate={{ scale: [1, 1.6], opacity: [0.7, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
          aria-hidden="true"
        >
          <path d={HEART_PATH} />
        </motion.svg>
        <motion.svg
          viewBox="0 0 24 24"
          className="absolute inset-0 w-full h-full drop-shadow-[0_10px_18px_rgba(216,140,154,0.55)]"
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="cueHeart" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#E0A3AC" />
              <stop offset="1" stopColor="#C57B89" />
            </linearGradient>
          </defs>
          <path d={HEART_PATH} fill="url(#cueHeart)" stroke="#C57B89" strokeWidth="0.6" />
        </motion.svg>
        <motion.svg
          viewBox="0 0 24 24"
          className="relative z-10 w-5 h-5 mt-1 transition-transform duration-500 group-hover:scale-110"
          fill="none"
          stroke="#FDFBF7"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{ y: [0, 4, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        >
          <path d="M6 10l6 6 6-6" />
        </motion.svg>
      </div>
    </motion.div>
  </motion.a>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const sphereY = useTransform(scrollY, [0, 700], [0, 140]);
  const softY = useTransform(scrollY, [0, 700], [0, -90]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const l1 = { x: useTransform(sx, (v) => v * 42), y: useTransform(sy, (v) => v * 30) };
  const l2 = { x: useTransform(sx, (v) => v * -26), y: useTransform(sy, (v) => v * -18) };
  const l3 = { x: useTransform(sx, (v) => v * 14), y: useTransform(sy, (v) => v * 10) };

  const handleMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-6 pt-20 pb-16"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: sphereY }}
        aria-hidden="true"
        className="absolute -top-32 -right-24 w-64 h-64 md:w-[34rem] md:h-[34rem] rounded-full opacity-40 md:opacity-70 animate-soft-pulse"
      >
        <div
          className="w-full h-full rounded-full"
          style={{
            background:
              "radial-gradient(circle at 32% 30%, #F3D9DC 0%, #E0A3AC 38%, #D88C9A 62%, #D4AF37 110%)",
            filter: "blur(2px)",
          }}
        />
      </motion.div>
      <motion.div
        style={{ y: softY }}
        aria-hidden="true"
        className="absolute -bottom-40 -left-32 w-[24rem] h-[24rem] rounded-full bg-[#D4AF37]/15 blur-3xl"
      />
      <motion.div
        aria-hidden="true"
        className="absolute top-1/3 left-[8%] w-40 h-40 rounded-full bg-[#E6C2C6]/40 blur-2xl"
      />

      <motion.div style={l1} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <FloatItem type="heart" filled color="#D88C9A" className="w-6 h-6 top-[20%] left-[13%]" delay={1.6} duration={7} drift={18} sway={10} />
        <FloatItem type="balloon" className="w-12 h-12 bottom-[22%] right-[8%] text-[#D88C9A] hidden md:block" delay={2} duration={9} drift={24} sway={12} spin={5} />
        <FloatItem type="heart" filled color="#D4AF37" className="w-4 h-4 top-[60%] left-[17%]" delay={2.3} duration={8} drift={14} sway={8} />
      </motion.div>

      <motion.div style={l2} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <FloatItem type="gift" className="w-11 h-11 top-[24%] right-[15%] text-[#C57B89] hidden md:block" delay={1.8} duration={10} drift={18} sway={10} spin={6} />
        <FloatItem type="envelope" className="w-9 h-9 bottom-[28%] left-[8%] text-[#D4AF37] hidden md:block" delay={2.1} duration={9} drift={14} sway={8} spin={6} />
        <FloatItem type="ring" className="w-8 h-8 bottom-[14%] right-[27%] text-[#D4AF37] hidden md:block" delay={2.5} duration={8} drift={12} sway={7} />
      </motion.div>

      <motion.div style={l3} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <FloatItem type="bow" className="w-10 h-10 top-[13%] right-[32%] text-[#E0A3AC]" delay={2.2} duration={11} drift={10} sway={6} spin={5} />
        <FloatItem type="heart" filled color="#E0A3AC" className="w-5 h-5 bottom-[16%] left-[25%]" delay={2.6} duration={9} drift={14} sway={8} />
        <FloatItem type="mug" className="w-8 h-8 top-[46%] right-[7%] text-[#E0A3AC] hidden md:block" delay={2.8} duration={10} drift={12} sway={7} spin={6} />
        <Twinkle className="w-5 h-5 top-[36%] left-[23%]" delay={0.6} />
        <Twinkle className="w-3.5 h-3.5 top-[18%] right-[24%]" delay={1.8} color="#D88C9A" />
        <Twinkle className="w-4 h-4 bottom-[32%] right-[18%]" delay={1.1} />
        <Twinkle className="w-3 h-3 bottom-[12%] left-[42%]" delay={2.4} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
        className="relative z-10"
      >
        <GiviLogo className="w-[85vw] md:w-[72rem] max-w-none [@media(max-height:840px)]:w-[36rem]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 text-center mt-10 md:mt-14 [@media(max-height:840px)]:mt-6">
        <MaskedLine delay={0.6}>
          <span
            className="text-[0.65rem] md:text-xs tracking-[0.55em] uppercase text-[#8A7D78] font-light pl-2"
            data-testid="hero-domain"
          >
            givi.oneeight.au
          </span>
        </MaskedLine>

        <h1 className="mt-6" data-testid="hero-heading">
          <MaskedLine delay={0.8}>
            <span className="block font-serif font-light text-[17vw] md:text-[9.5rem] leading-[0.92] tracking-tight text-[#2C2421] [@media(max-height:840px)]:text-7xl [@media(max-height:840px)]:md:text-7xl">
              Coming
            </span>
          </MaskedLine>
          <MaskedLine delay={0.95}>
            <span className="block font-serif italic font-medium text-[17vw] md:text-[9.5rem] leading-[1.02] tracking-tight text-givi-gradient text-shimmer pr-3 [@media(max-height:840px)]:text-7xl [@media(max-height:840px)]:md:text-7xl">
              Soon
            </span>
          </MaskedLine>
        </h1>

        <HeartScrollCue />

        <MaskedLine delay={1.15} className="mt-8 [@media(max-height:840px)]:mt-4">
          <p className="max-w-md mx-auto text-base md:text-lg font-light leading-relaxed text-[#2C2421]/70">
            Personalised gifts, celebrations, custom creations & little luxuries —
            made for every moment worth celebrating.
          </p>
        </MaskedLine>
      </motion.div>
    </section>
  );
}
